import { create } from 'zustand';
import type { User } from '../types/auth.types';

interface AuthStore {
	// State
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	isInitialized: boolean;
	sessionExpiry: number | null;
	enforcePasswordChange: boolean;

	// Actions
	setUser: (user: User | null, enforcePasswordChange?: boolean) => void;
	setLoading: (loading: boolean) => void;
	setInitialized: (initialized: boolean) => void;
	setSessionExpiry: (expiry: number | null) => void;
	login: (user: User, enforcePasswordChange?: boolean) => void;
	logout: () => void;
	updateUser: (updates: Partial<User>) => void;
	initialize: () => void;
}

// Check if an auth session is active (tokens are in HTTP-only cookies)
const hasTokens = () => {
	if (typeof window === 'undefined') return false;
	return !!localStorage.getItem('auth_session_active');
};

export const useAuthStore = create<AuthStore>((set, get) => ({
	// Initial state - check for existing tokens
	user: null,
	isAuthenticated: false,
	isLoading: hasTokens(), // Only loading if we have tokens to verify
	isInitialized: !hasTokens(), // Already initialized if no tokens
	sessionExpiry: null,
	enforcePasswordChange: false,

	// Actions
	setUser: (user) => {
		// Only update if user is actually defined
		if (user && user.id) {
			set({
				user,
				isAuthenticated: true,
				isLoading: false,
				isInitialized: true,
			});
		} else {
			set({
				user: null,
				isAuthenticated: false,
				isLoading: false,
				isInitialized: true,
			});
		}
	},

	setLoading: (loading) => set({ isLoading: loading }),

	setInitialized: (initialized) => set({ isInitialized: initialized }),

	setSessionExpiry: (expiry) => set({ sessionExpiry: expiry }),

	login: (user, enforcePasswordChange) => {
		set({
			user,
			isAuthenticated: true,
			isLoading: false,
			isInitialized: true,
			enforcePasswordChange: enforcePasswordChange ?? false,
		});
	},

	logout: () => {
		set({
			user: null,
			isAuthenticated: false,
			isLoading: false,
			sessionExpiry: null,
			enforcePasswordChange: false,
		});
	},

	updateUser: (updates) => {
		const currentUser = get().user;
		if (currentUser) {
			set({ user: { ...currentUser, ...updates } });
		}
	},

	initialize: () => {
		// Skip if already authenticated (e.g. after login, navigating to change-password)
		if (get().isAuthenticated) {
			set({ isInitialized: true, isLoading: false });
			return;
		}

		const hasTokens = !!localStorage.getItem('auth_session_active');

		if (!hasTokens) {
			set({
				isLoading: false,
				isInitialized: true,
				isAuthenticated: false,
				user: null,
			});
		} else {
			// Has tokens - mark as loading and let useAuth hook verify them
			set({
				isLoading: true,
				isInitialized: true, // Mark as initialized so UI can proceed
				isAuthenticated: false, // Will be set to true by useAuth if tokens are valid
			});
		}
	},
}));

// Selectors
export const selectUser = (state: AuthStore) => state.user;
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated;
export const selectIsAuthLoading = (state: AuthStore) => state.isLoading;
export const selectIsInitialized = (state: AuthStore) => state.isInitialized;
export const selectEnforcePasswordChange = (state: AuthStore) => state.enforcePasswordChange;
